import React from "react";
import * as yup from "yup";

import {
  cellStyle,
  headingStyle,
} from "components/CostingGrid/EditableGrid/shared/utils";
import { OverlayTrigger, Tooltip, Dropdown, Row, Col } from "react-bootstrap";
import {
  CustomDropdownMenu,
  CustomToggle,
} from "components/CostingGrid/EditableGrid/shared/CustomDropdowns";
import CostingHeaderCell from "components/CostingGrid/EditableGrid/shared/CostingHeaderCell";
import { isNumeric } from "utils/NumbersUtil";

import {
  GovernanceExceptionApprovalPopover,
  getBagdeVariant,
} from "components/OpportunityGovernance/shared";

const getSubContractorsHeaders = (
  opportunity,
  subContractorExceptions,
  updateExceptionStatus,
) => {
  const headers = [
    {
      id: "colTag",
      header: (props) => <td className={cellStyle + " text-center"}>A </td>,
      accessorKey: "colTag",
      cell: (props) => (
        <td className={cellStyle + " text-center"}>
          {props.getValue()}
          <span className="float-end cursor-pointer">
            <Dropdown>
              <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>
              <Dropdown.Menu as={CustomDropdownMenu} align={"right"}>
                <Dropdown.Item
                  onClick={() =>
                    props.table.options.meta?.handleAddNewRow(
                      props.row.index + 1,
                    )
                  }
                >
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>Add Subcontractor</Tooltip>}
                  >
                    <span className="btn btn-sm p-0" role="button">
                      <span className="material-icons-outlined md-18">add</span>
                    </span>
                  </OverlayTrigger>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() =>
                    props.table.options.meta?.handleDeleteRow(
                      props.row.original.id,
                    )
                  }
                >
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>Delete Subcontractor</Tooltip>}
                  >
                    <span className="btn btn-sm p-0" role="button">
                      <span className="material-icons-outlined md-18">
                        delete
                      </span>
                    </span>
                  </OverlayTrigger>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </span>
        </td>
      ),
      footer: (props) => props.column.id,
    },
    {
      id: "total",
      header: () => (
        <td
          className={headingStyle.replace(/bg-[a-zA-Z]+/g, "bg-tertiary")}
          xs={6}
        >
          Total - Subcontractors
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "total", // accessor is the "key" in the data
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "custom",
          disabled: true,
          component: ({
            row,
            column,
            table,
            cell,
            value,
            setValue,
            validationError,
            handleOnBlur,
            handleOnInput,
          }) => {
            const subContractorCostException = subContractorExceptions.find(
              (item) => String(item.uniqueId) === String(row.original.name),
            );
            const ValdationPopover = ({ ...props }) => (
              <GovernanceExceptionApprovalPopover
                {...props}
                exception={subContractorCostException}
                updateExceptionStatus={updateExceptionStatus}
              />
            );
            return (
              <Row>
                <Col className="text-end">
                  <span className="position-relative p-0">
                    {subContractorCostException ? (
                      <>
                        <span className="position-absolute top-0 left-0">
                          <OverlayTrigger
                            overlay={ValdationPopover}
                            trigger={"click"}
                          >
                            <span
                              className="btn btn-xs p-0 m-0"
                              data-title="Click to view the exception details"
                            >
                              <span className="material-icons-outlined md-18 text-danger align-baseline fw-bold">
                                priority_high
                              </span>
                            </span>
                          </OverlayTrigger>
                        </span>
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              {subContractorCostException?.rule?.description}
                            </Tooltip>
                          }
                        >
                          <span
                            className={`d-inline-block w-100 pe-1
                               border-tertiary bg-${getBagdeVariant(
                                 subContractorCostException.status,
                               )} bg-opacity-50`}
                          >
                            {value}
                          </span>
                        </OverlayTrigger>
                      </>
                    ) : (
                      <span className={"d-inline-block w-100 pe-1"}>
                        {value}
                      </span>
                    )}
                  </span>
                </Col>
              </Row>
            );
          },
        },
        cellClasses: cellStyle.replace(/bg-[a-zA-Z]+/g, "bg-tertiary"),
      },
    },
    {
      id: "id",
      header: () => (
        <td className={headingStyle} xs={6}>
          Subcontractor Name
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "name",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "text",
          validationFn: (value, fieldName, row, table) => {
            const otherRows = table
              .getRowModel()
              .rows.filter(
                (rowElem) => rowElem.original.id !== row.original.id,
              );
            const isExistingRow = otherRows.some(
              (rowElem) => rowElem.original.name === value,
            );
            return isExistingRow ? "Subcontractor already exists" : "";
          },
        },
      },
    },
    {
      id: "unit",
      header: () => (
        <td className={headingStyle} xs={6}>
          Unit (day,lump,etc)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "unit",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "text",
          dependsOnCols: ["id"],
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Local Currency
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "localCurrency",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "text",
          disabled: true,
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Local Cost
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "localCost",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          dependsOnCols: ["id"],
          validationFn: (value) =>
            yup.number().min(0).isValidSync(value)
              ? ""
              : "Value needs to be a positive value",
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Contingency (%, if applicable)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "contingencyPercentage",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          validationFn: (value, _) =>
            yup.number().min(0).max(100).isValidSync(value)
              ? ""
              : "Value needs to be a % (0 to 100)",
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Mark Up (at{" "}
          {isNumeric(opportunity?.suppliersMarkup)
            ? opportunity?.suppliersMarkup
            : "n/a "}
          %)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "markUp",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          disabled: true,
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Adjusted Mark Up % (+/-)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "adjustedMarkUpPercentage",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          validationFn: (value, _) =>
            yup.number().min(-100).max(100).isValidSync(value)
              ? ""
              : "Value needs to be a % (-100 to 100)",
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Total Mark Up
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "totalMarkUp",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          disabled: true,
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Total Mark Up (Currency Rate)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "totalMarkUpCurrencyRate",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          disabled: true,
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Tax - GST/VAT (%, if applicable)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "taxVat",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          validationFn: (value, _) =>
            yup.number().min(0).max(100).isValidSync(value)
              ? ""
              : "Value needs to be a % (0 to 100)",
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Tax - WHT (%, if applicable)
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "taxWht",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          validationFn: (value, _) =>
            yup.number().min(0).max(100).isValidSync(value)
              ? ""
              : "Value needs to be a % (0 to 100)",
        },
      },
    },
    {
      header: () => (
        <td className={headingStyle} xs={6}>
          Currency Cost
        </td>
      ),
      cell: CostingHeaderCell,
      accessorKey: "currencyCost",
      footer: (props) => props.column.id,
      meta: {
        cellInputProps: {
          type: "number",
          disabled: true,
        },
      },
    },
  ];

  return headers;
};

export default getSubContractorsHeaders;
